import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ThemeContext = createContext(null);

const FONT_PAIRINGS = {
  journal: { heading: "'Cormorant Garamond', serif", body: "'Nunito', sans-serif", hand: "'Caveat', cursive" },
  modern:  { heading: "'Inter', sans-serif",        body: "'Inter', sans-serif",  hand: "'Kalam', cursive" },
  zine:    { heading: "'Playfair Display', serif",  body: "'Work Sans', sans-serif", hand: "'Homemade Apple', cursive" },
  soft:    { heading: "'Fraunces', serif",          body: "'DM Sans', sans-serif", hand: "'Caveat', cursive" },
};

const FONT_CDN_MAP = {
  journal: "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=Nunito:wght@300;400;500;600;700;800&family=Caveat:wght@400;500;600;700&display=swap",
  modern:  "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Kalam:wght@300;400;700&display=swap",
  zine:    "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400&family=Work+Sans:wght@300;400;500;600;700&family=Homemade+Apple&display=swap",
  soft:    "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500;9..144,600;9..144,700&family=DM+Sans:wght@300;400;500;600;700&family=Caveat:wght@400;500;600;700&display=swap",
};

function loadFontFor(id) {
  if (!id) return;
  const linkId = `ascend-font-${id}`;
  if (document.getElementById(linkId)) return;
  const url = FONT_CDN_MAP[id];
  if (!url) return;
  const link = document.createElement("link");
  link.id = linkId;
  link.rel = "stylesheet";
  link.href = url;
  document.head.appendChild(link);
}

function hexToRgb(h) {
  if (!h) return { r: 0, g: 0, b: 0 };
  const v = h.replace("#", "");
  const full = v.length === 3 ? v.split("").map((c) => c + c).join("") : v;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
function relLum({ r, g, b }) {
  const f = (v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}
export function contrastRatio(fg, bg) {
  const l1 = relLum(hexToRgb(fg));
  const l2 = relLum(hexToRgb(bg));
  const [a, b] = l1 > l2 ? [l1, l2] : [l2, l1];
  return (a + 0.05) / (b + 0.05);
}

function pickBackgroundForToday(theme) {
  const list = theme.active_background_ids || [];
  if (!list.length || theme.background_mode === "none") return null;
  if (theme.background_mode === "single") return list[0];
  // rotate daily: day-of-year modulo length
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const diff = now - start;
  const day = Math.floor(diff / (1000 * 60 * 60 * 24));
  return list[day % list.length];
}

function getBackendBase() {
  return process.env.REACT_APP_BACKEND_URL || "";
}

export function applyThemeToDom(theme, token) {
  if (!theme) return;
  const root = document.documentElement;
  const p = theme.palette || {};
  const map = {
    "--primary": p.primary,
    "--accent": p.accent,
    "--bg": p.background_base,
    "--bg-2": p.background_gradient_stop,
    "--surface": p.surface_card,
    "--surface-2": p.surface_card,
    "--text": p.text_primary,
    "--text-muted": p.text_muted,
    "--grad-a": p.background_base,
    "--grad-b": p.background_gradient_stop,
    "--grad-c": p.background_gradient_stop,
    "--banner-tint": p.primary + "2E",
    "--border": (p.text_primary || "#000") + "18",
    "--ring": (p.primary || "#000") + "59",
    "--primary-soft": p.background_gradient_stop,
  };
  Object.entries(map).forEach(([k, v]) => { if (v) root.style.setProperty(k, v); });

  // Sketchbook portrait frame — subtle ink-wash colours that adapt to preset.
  // Ink Wash uses grayscale sumi-e (text_primary at 30%). Sakura/Ghibli pick
  // the warmer accent. Dusk uses the richer primary. Custom themes default to
  // the primary hue.
  const preset = String(theme.aesthetic_preset || "").toLowerCase();
  let strokeMix, washVar;
  if (preset.includes("ink")) {
    strokeMix = `color-mix(in oklab, ${p.text_primary || "#000"} 30%, transparent)`;
    washVar = `color-mix(in oklab, ${p.text_primary || "#000"} 8%, transparent)`;
  } else if (preset.includes("sakura") || preset.includes("ghibli") || preset.includes("meadow")) {
    strokeMix = `color-mix(in oklab, ${p.accent || p.primary || "#000"} 50%, transparent)`;
    washVar = `color-mix(in oklab, ${p.accent || p.primary || "#000"} 15%, transparent)`;
  } else if (preset.includes("dusk") || preset.includes("violet")) {
    strokeMix = `color-mix(in oklab, ${p.primary || "#000"} 55%, transparent)`;
    washVar = `color-mix(in oklab, ${p.primary || "#000"} 18%, transparent)`;
  } else {
    strokeMix = `color-mix(in oklab, ${p.primary || "#000"} 55%, transparent)`;
    washVar = `color-mix(in oklab, ${p.primary || "#000"} 15%, transparent)`;
  }
  root.style.setProperty("--sketchbook-stroke", strokeMix);
  root.style.setProperty("--sketchbook-wash", washVar);

  // Fonts
  const fp = FONT_PAIRINGS[theme.font_pairing_id] || FONT_PAIRINGS.journal;
  loadFontFor(theme.font_pairing_id || "journal");
  root.style.setProperty("--font-display", fp.heading);
  root.style.setProperty("--font-body", fp.body);
  root.style.setProperty("--font-hand", fp.hand);
  root.style.setProperty("--card-radius", theme.card_style === "sharp" ? "0.35rem" : "1.5rem");
  root.style.setProperty("--card-radius-2", theme.card_style === "sharp" ? "0.25rem" : "1.25rem");
  root.style.setProperty("--card-shadow",
    theme.shadow_depth === "none" ? "none"
    : theme.shadow_depth === "warm" ? `0 24px 44px -22px ${p.primary || "#000"}55`
    : "0 12px 40px -20px rgba(50, 30, 40, 0.18)");
  root.style.setProperty("--card-surface-alpha", theme.card_surface === "glass" ? "0.72" : "1");
  root.style.setProperty("--card-backdrop", theme.card_surface === "glass" ? "blur(14px)" : "none");

  // Background image
  const chosenId = pickBackgroundForToday(theme);
  let bgImage = "";
  if (chosenId) {
    if (chosenId.startsWith("curated-")) {
      const list = window.__ascend_curated_bgs || [];
      const found = list.find((b) => b.id === chosenId);
      if (found?.css) bgImage = found.css;
    } else {
      // Same-origin cookie auth carries the token — no query param needed.
      bgImage = `url("${getBackendBase()}/api/media/${chosenId}")`;
    }
  }
  root.style.setProperty("--user-bg", bgImage || "none");
  root.style.setProperty("--user-bg-opacity", String((theme.background_opacity ?? 30) / 100));
  root.style.setProperty("--user-bg-blur", `${theme.background_blur ?? 0}px`);
}

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const [theme, setTheme] = useState(null);
  const [defaults, setDefaults] = useState(null);

  const load = useCallback(async () => {
    if (!user || typeof user !== "object") return;
    try {
      const [t, d] = await Promise.all([api.get("/theme"), api.get("/theme/defaults")]);
      setTheme(t.data);
      setDefaults(d.data);
      // Cache curated backgrounds globally so applyThemeToDom can find gradients.
      window.__ascend_curated_bgs = d.data.backgrounds || [];
      applyThemeToDom(t.data, localStorage.getItem("ascend_token"));
    } catch {}
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (next) => {
    setTheme(next);
    applyThemeToDom(next, localStorage.getItem("ascend_token"));
    const { data } = await api.put("/theme", next);
    setTheme(data);
    return data;
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, defaults, setTheme, save, reload: load, applyThemeToDom }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
